"use client";

import { Check, Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";

import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/hooks/useUser";
import { useAnalytics } from "@/lib/analytics";
import { ANNUAL_DISCOUNT_PERCENT, PRICING_PLANS } from "@/lib/pricing";

import {
	Button,
	GlassContainer,
	Label,
	Switch,
	Tag,
} from "@meetploy/storybook";

interface SubscriptionStatus {
	plan: "free" | "pro";
	subscriptionId: string | null;
	planExpiresAt: string | null;
	subscriptionCancelled: boolean;
}

export function PricingPlans() {
	const { user } = useUser();
	const { toast } = useToast();
	const router = useRouter();
	const { capture } = useAnalytics();
	const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
		"monthly",
	);
	const [loading, setLoading] = useState<string | null>(null);
	const [subscriptionStatus, setSubscriptionStatus] =
		useState<SubscriptionStatus | null>(null);
	const sectionRef = useRef<HTMLElement | null>(null);
	const hasTrackedView = useRef(false);

	// Fetch subscription status for authenticated users
	useEffect(() => {
		if (user) {
			void fetchSubscriptionStatus();
		}
	}, [user]);

	// Fire `pricing_viewed` once, when the section actually enters the viewport.
	useEffect(() => {
		const section = sectionRef.current;
		if (!section) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const isVisible = entries.some((entry) => entry.isIntersecting);
				if (!isVisible || hasTrackedView.current) {
					return;
				}
				hasTrackedView.current = true;
				capture("pricing_viewed", {
					source: window.location.pathname,
					billing_cycle: billingCycle,
				});
				observer.disconnect();
			},
			{ threshold: 0.3 },
		);

		observer.observe(section);
		return () => {
			observer.disconnect();
		};
		// `billingCycle` is read for the initial view only, so it is not a dependency.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [capture]);

	const fetchSubscriptionStatus = async () => {
		try {
			const response = await fetch("/api/subscriptions/status", {
				credentials: "include",
			});
			if (response.ok) {
				const status = (await response.json()) as SubscriptionStatus;
				setSubscriptionStatus(status);
			}
		} catch (error) {
			console.error("Failed to fetch subscription status:", error);
		}
	};

	const handleCreateProSubscription = async () => {
		if (!user) {
			router.push("/signup?nextUrl=/pricing?plan=pro");
			return;
		}

		setLoading("pro");
		capture("checkout_started", {
			plan: "pro",
			billing_cycle: billingCycle,
			location: "pricing",
		});

		try {
			const response = await fetch(
				"/api/subscriptions/create-pro-subscription",
				{
					method: "POST",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						billingCycle: billingCycle === "annual" ? "yearly" : "monthly",
					}),
				},
			);

			if (!response.ok) {
				const error = await response.text();
				throw new Error(error);
			}

			const { checkoutUrl } = (await response.json()) as {
				checkoutUrl: string;
			};

			// Redirect to Stripe Checkout
			window.location.href = checkoutUrl;
		} catch (error: unknown) {
			capture("checkout_failed", {
				plan: "pro",
				error_type:
					error instanceof Error ? error.message : "unknown_checkout_error",
			});
			toast({
				title: "Subscription failed",
				description:
					error instanceof Error
						? error.message
						: "Failed to create checkout session. Please try again.",
				variant: "destructive",
			});
			setLoading(null);
		}
	};

	const handleCancelSubscription = async () => {
		if (!subscriptionStatus?.subscriptionId) {
			return;
		}

		setLoading("cancel");
		try {
			const response = await fetch(
				"/api/subscriptions/cancel-pro-subscription",
				{
					method: "POST",
					credentials: "include",
				},
			);

			if (!response.ok) {
				const error = await response.text();
				throw new Error(error);
			}

			capture("subscription_cancelled", { plan: "pro" });
			toast({
				title: "Subscription cancelled",
				description:
					"Your subscription will remain active until the end of the billing period.",
			});
			await fetchSubscriptionStatus();
		} catch (error: unknown) {
			toast({
				title: "Cancellation failed",
				description:
					error instanceof Error
						? error.message
						: "Failed to cancel subscription. Please try again.",
				variant: "destructive",
			});
		} finally {
			setLoading(null);
		}
	};

	const handleResumeSubscription = async () => {
		if (!subscriptionStatus?.subscriptionId) {
			return;
		}

		setLoading("resume");
		try {
			const response = await fetch(
				"/api/subscriptions/resume-pro-subscription",
				{
					method: "POST",
					credentials: "include",
				},
			);

			if (!response.ok) {
				const error = await response.text();
				throw new Error(error);
			}

			capture("subscription_resumed", { plan: "pro" });
			toast({
				title: "Subscription resumed",
				description: "Your Pro subscription has been reactivated.",
			});
			await fetchSubscriptionStatus();
		} catch (error: unknown) {
			toast({
				title: "Resume failed",
				description:
					error instanceof Error
						? error.message
						: "Failed to resume subscription. Please try again.",
				variant: "destructive",
			});
		} finally {
			setLoading(null);
		}
	};

	const handlePlanSelection = (planName: string) => {
		switch (planName) {
			case "Self-Host":
				capture("outbound_link_clicked", {
					destination: "docs",
					link_url: "https://docs.meetploy.com",
					link_text: "View Docs",
					location: "pricing",
				});
				router.push("https://docs.meetploy.com");
				return;
			case "Enterprise":
				capture("outbound_link_clicked", {
					destination: "email",
					link_url: "mailto:contact@meetploy.com",
					link_text: "Contact Sales",
					location: "pricing",
				});
				router.push("mailto:contact@meetploy.com");
				return;
			case "Pro":
				void handleCreateProSubscription();
				return;
		}

		if (!user) {
			router.push("/signup");
		} else {
			router.push("/dashboard");
		}
	};

	const plans = PRICING_PLANS.map((plan) => ({
		...plan,
		cta:
			plan.name === "Self-Host"
				? "View Docs"
				: plan.name === "Enterprise"
					? "Contact Sales"
					: plan.name === "Pro"
						? subscriptionStatus?.plan === "pro"
							? subscriptionStatus.subscriptionCancelled
								? "Resume"
								: "Cancel"
							: "Upgrade to Pro"
						: user
							? "Current Plan"
							: "Get Started",
		disabled: plan.name === "Free" && subscriptionStatus?.plan === "free",
	}));

	const discount = ANNUAL_DISCOUNT_PERCENT;

	return (
		<section
			ref={sectionRef}
			className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-border"
			id="pricing"
		>
			<div className="text-center mb-12">
				<div className="flex justify-center mb-6">
					<Tag variant="default">Pricing</Tag>
				</div>
				<h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
					Run one agent or ten thousand
				</h2>
				<p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
					Start free on our cloud, or self-host the whole platform at no cost.
					No hidden fees, and no charge for the infrastructure you already own.
				</p>

				<div className="flex items-center justify-center gap-4 mb-12">
					<Label
						htmlFor="billing-toggle"
						className={
							billingCycle === "monthly"
								? "font-medium"
								: "text-muted-foreground"
						}
					>
						Monthly
					</Label>
					<Switch
						id="billing-toggle"
						checked={billingCycle === "annual"}
						onCheckedChange={(checked: boolean) => {
							const nextCycle = checked ? "annual" : "monthly";
							setBillingCycle(nextCycle);
							capture("billing_cycle_toggled", {
								billing_cycle: nextCycle,
							});
						}}
					/>
					<div className="flex items-center gap-2">
						<Label
							htmlFor="billing-toggle"
							className={
								billingCycle === "annual"
									? "font-medium"
									: "text-muted-foreground"
							}
						>
							Annual
						</Label>
						<Tag variant="success">Save {discount}%</Tag>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
				{plans.map((plan) => {
					const isCurrentPlan =
						subscriptionStatus?.plan === plan.name.toLowerCase();
					const isLoading =
						loading === plan.name.toLowerCase() ||
						(plan.name === "Pro" &&
							(loading === "cancel" || loading === "resume"));

					return (
						<GlassContainer
							key={plan.name}
							rounded="2xl"
							shadow="lg"
							className={`p-6 h-full flex flex-col relative transition-shadow ${
								plan.popular
									? "ring-2 ring-foreground"
									: "hover:ring-foreground/25"
							} ${isCurrentPlan ? "ring-2 ring-primary" : ""}`}
						>
							{plan.popular && (
								<div className="absolute -top-3 right-6 bg-foreground text-background text-[0.625rem] font-semibold uppercase tracking-widest px-3 py-1">
									Free 7-day trial
								</div>
							)}
							{isCurrentPlan && (
								<div className="absolute -top-3 left-6">
									<Tag variant="blue">Current Plan</Tag>
								</div>
							)}

							<div className="mb-8">
								<h3 className="text-lg font-semibold mb-2">{plan.name}</h3>
								<p className="text-sm text-muted-foreground mb-6 h-10">
									{plan.description}
								</p>

								<div className="flex items-baseline gap-1">
									<span className="text-4xl font-bold">
										{plan.price[billingCycle]}
									</span>
									{plan.price[billingCycle] !== "Custom" &&
										plan.price[billingCycle] !== "Free" &&
										plan.price[billingCycle] !== "$0" && (
											<span className="text-sm text-muted-foreground">
												/{billingCycle === "monthly" ? "month" : "year"}
											</span>
										)}
								</div>
								{subscriptionStatus?.subscriptionCancelled &&
									plan.name === "Pro" && (
										<Tag variant="warning" className="mt-2">
											Cancelled - Active until{" "}
											{subscriptionStatus.planExpiresAt
												? new Date(
														subscriptionStatus.planExpiresAt,
													).toLocaleDateString()
												: "end of billing period"}
										</Tag>
									)}
							</div>

							<div className="flex-grow mb-8">
								<ul className="space-y-3">
									{plan.features.map((feature) => (
										<li
											key={feature}
											className="flex items-start gap-3 text-sm text-muted-foreground"
										>
											<Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
											<span>{feature}</span>
										</li>
									))}
								</ul>
							</div>

							<Button
								className={`w-full ${plan.popular ? "bg-foreground text-background hover:bg-foreground/90" : ""}`}
								variant={plan.popular ? "default" : "outline"}
								disabled={plan.disabled || isLoading}
								onClick={() => {
									capture("plan_selected", {
										plan_name: plan.name,
										billing_cycle: billingCycle,
										cta_text: plan.cta,
										is_authenticated: !!user,
									});
									if (
										plan.name === "Pro" &&
										subscriptionStatus?.plan === "pro"
									) {
										if (subscriptionStatus.subscriptionCancelled) {
											void handleResumeSubscription();
										} else {
											void handleCancelSubscription();
										}
									} else {
										handlePlanSelection(plan.name);
									}
								}}
							>
								{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								{plan.cta}
							</Button>
						</GlassContainer>
					);
				})}
			</div>

			<div className="mt-16 text-center">
				<p className="text-sm text-muted-foreground">
					All plans include access to our API, docs, and community support.
					<br />
					Need a custom solution?{" "}
					<a
						href="mailto:contact@meetploy.com"
						className="text-foreground hover:underline"
					>
						Contact our sales team
					</a>
					.
				</p>
			</div>
		</section>
	);
}
